<script setup lang="ts">
import { TypedMoney } from '@commercetools/platform-sdk';
import { ProposedDelivery } from '@nuts/auto-delivery-sdk';
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { multiply } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useSessionStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref } from 'vue';

import IntervalSelector from '@/components/account/auto-delivery/details/IntervalSelector.vue';
import InfoIcon from '@/components/base/assets/InfoIcon.vue';
import Radio from '@/components/base/form/Radio.vue';
import Modal from '@/components/base/layout/Modal.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import ImpulseUpsellInformation from '@/components/pdp/impulse-upsell/ImpulseUpsellInformation.vue';
import ImpulseUpsellSuccess from '@/components/pdp/impulse-upsell/ImpulseUpsellSuccess.vue';
import { useCallback } from '@/composables/useCallback';
import { PriceFragment } from '@/graphql/fragments/price';
import { AnalyticsMetadata } from '@/lib/personalization/dynamicYield';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { sendProductSubscriptionCreatedEvent } from '@/utils/analytics/productSubscriptionCreatedEvent';
import { Money } from '@/utils/money';
import { getMoneyValue } from '@/utils/product';

type CurrentView = 'impulse-upsell' | 'info' | 'success' | false;

interface Props {
  delivery: ProposedDelivery;
  isOpen?: boolean;
  name: string;
  priceOptions: PriceFragment[];
  productKey: string;
  quantity?: number;
  reportingCategory?: string;
  sku: string;
  trackingLocation: 'PDP' | 'Easy Reorder';
  total?: TypedMoney;
  wholesale?: boolean;
}

const props = withDefaults(defineProps<Props>(), { quantity: 1 });
const emit = defineEmits(['handle-close', 'handle-open']);

const { createSubscription, getActiveSubscriptions, getOrders } = useAutoDelivery();
const { activeSubscriptionsBySKU, autoDeliveryDefaultChannel } = storeToRefs(useAutoDelivery());

const selection = reactive({
  mode: 'recurring' as 'one-time' | 'recurring',
  frequency: 6,
});
const activeView = ref<CurrentView>('impulse-upsell');

const formattedNextShipOn = computed(() => dayjs(props.delivery.shipOn).format('dddd, MMM D'));

const autoDeliveryUpsellAttribution = useSessionStorage<{
  analyticsMetadata?: AnalyticsMetadata;
}>('autoDeliveryUpsell.attribution', {});

const setActiveView = (view: CurrentView) => {
  activeView.value = view;
};

const addToUpcomingDelivery = useCallback(async () => {
  await createSubscription({
    active: true,
    interval: selection.mode === 'recurring' ? selection.frequency * 7 : undefined,
    nextShipOn: props.delivery.shipOn,
    offerLocation: props.trackingLocation,
    offerType: selection.mode === 'recurring' ? 'IU Recurring' : 'IU Upsell',
    quantity: props.quantity,
    shippingAddressId: props.delivery.shippingAddressId,
    sku: props.sku,
  });

  await Promise.all([
    getOrders({
      preloadPaymentMethod: false,
      preloadShippingAddresses: false,
    }),
    getActiveSubscriptions({
      preloadPaymentMethod: false,
      preloadShippingAddresses: false,
    }),
  ]);

  const createdSubscription = activeSubscriptionsBySKU.value.get(props.sku)?.[0];

  if (createdSubscription) {
    const { analyticsMetadata } = autoDeliveryUpsellAttribution.value;

    sendProductSubscriptionCreatedEvent({
      analyticsMetadata,
      pageSection:
        props.trackingLocation === 'PDP'
          ? 'Auto-Delivery Sign-Up Modal'
          : 'Auto-Delivery Sign-Up Modal - Easy Reorder',
      productKey: props.productKey,
      subscription: createdSubscription,
    });
  }

  setActiveView('success');
});

const listPrice = computed(() => props.priceOptions.find((price) => !price.channel)!);
const channelPrice = computed(
  () =>
    props.priceOptions.find(
      (price) => price.channel?.key === autoDeliveryDefaultChannel.value?.key,
    ) ?? listPrice.value,
);

const selectedPrice = computed(() =>
  selection.mode === 'recurring' && !props.wholesale ? channelPrice.value : listPrice.value,
);

const listValue = computed(() =>
  multiply(
    getMoneyValue(listPrice.value, props.quantity, { ignoreDiscounted: true }),
    props.quantity,
  ),
);
const selectedValue = computed(() =>
  multiply(
    getMoneyValue(selectedPrice.value, props.quantity, { ignoreDiscounted: true }),
    props.quantity,
  ),
);

const productDiscountNotice = computed(() => {
  if (!listPrice.value.discounted || channelPrice.value.discounted) return undefined;

  let message = 'The discounted price does not qualify for Auto-Delivery subscription';
  if (!props.wholesale) {
    message += `, but you can still enjoy ${autoDeliveryDefaultChannel.value.discount}% off of the listing price`;
  }

  return `${message}.`;
});

const handleClose = () => {
  if (activeView.value !== 'info') {
    emit('handle-close');
  }
  setActiveView('impulse-upsell');
};
</script>

<template>
  <Modal
    aria-labelledby="add-to-auto-delivery-title"
    border-radius="rounded-t-2xl sm:rounded-xl"
    breakpoint="sm"
    data-test="impulse-upsell-modal"
    :hasControls="false"
    :isOpen="isOpen"
    width="sm:max-w-lg"
    @handle-cancel="handleClose"
    @handle-close="handleClose"
  >
    <template #header>
      <Header4 headerTag="span" id="add-to-auto-delivery-title">
        <template v-if="activeView === 'info'">What do these options mean?</template>
        <template v-else-if="activeView === 'impulse-upsell'">
          Add this item to your {{ formattedNextShipOn }} order
        </template>
      </Header4>
    </template>
    <template #body>
      <ImpulseUpsellInformation v-if="activeView === 'info'" />
      <ImpulseUpsellSuccess
        v-else-if="activeView === 'success'"
        :delivery="delivery"
        :location="trackingLocation"
        @handle-close="handleClose"
      />
      <template v-else>
        <div
          v-if="productDiscountNotice"
          class="flex gap-4 p-4 mt-6 border border-solid shadow rounded-3xl border-neutral-300"
        >
          <InfoIcon :size="[20, 24]" />
          <SmallBodyText class="font-semibold">{{ productDiscountNotice }}</SmallBodyText>
        </div>
        <fieldset class="mt-6">
          <legend class="flex justify-between text-xs sm:text-sm">
            <span>
              <strong>Options:</strong>
              <UnstyledButton class="ml-1" @click="setActiveView('info')">
                <img
                  class="object-contain w-4 h-4"
                  alt="Learn more"
                  src="@/assets/pdp/info-icon-gray.svg"
                />
              </UnstyledButton>
            </span>
            <span class="flex items-center">
              <span class="font-bold">
                {{ money(selectedValue) }}
              </span>
              <del
                v-if="!Money.equals(selectedPrice.value, listPrice.value)"
                class="ml-2 line-through text-neutral-600"
              >
                {{ money(listValue) }}
              </del>
            </span>
          </legend>
          <div
            class="mt-2 h-14 items-center cursor-pointer flex justify-between rounded-md border border-solid w-full px-2.5"
            :class="
              selection.mode === 'recurring'
                ? 'border-black bg-nuts-neutral-100'
                : 'border-nuts-neutral-200 bg-white'
            "
            @click="selection.mode = 'recurring'"
          >
            <Radio
              v-model="selection.mode"
              :disabled="addToUpcomingDelivery.isPending"
              name="mode"
              value="recurring"
              data-test="auto-delivery-option"
            >
              <span class="flex items-center text-sm leading-5 sm:text-base sm:leading-5">
                <img
                  class="sm:h-4 h-3.5"
                  alt="Auto Delivery"
                  src="@/assets/auto-delivery/logo-gray.svg"
                />
                <span class="ml-1">Save {{ autoDeliveryDefaultChannel.discount }}%</span>
              </span>
            </Radio>
            <IntervalSelector
              v-model="selection.frequency"
              :disabled="selection.mode === 'one-time'"
              variant="default"
            />
          </div>
          <div
            class="mt-2 h-14 items-center cursor-pointer flex justify-between rounded-md border border-solid w-full px-2.5"
            :class="
              selection.mode === 'one-time'
                ? 'border-black bg-nuts-neutral-100'
                : 'border-nuts-neutral-200 bg-white'
            "
            @click="selection.mode = 'one-time'"
          >
            <Radio
              v-model="selection.mode"
              :disabled="addToUpcomingDelivery.isPending"
              name="mode"
              value="one-time"
              data-test="one-time-option"
            >
              <span class="flex items-center text-sm leading-5 sm:text-base sm:leading-5">
                One-Time
                <img
                  class="sm:h-4 h-3.5 ml-1"
                  alt="Auto Delivery"
                  src="@/assets/auto-delivery/logo-gray.svg"
                />
              </span>
            </Radio>
            <span class="hidden text-sm sm:visible sm:inline-block">
              Ship this product in the <strong>same box</strong>
            </span>
          </div>
        </fieldset>
      </template>
    </template>
    <template #modal-controls>
      <div v-if="activeView === 'impulse-upsell'" class="flex items-center justify-between mt-8">
        <ThemedButton
          :disabled="addToUpcomingDelivery.isPending"
          theme="white"
          @click.prevent="handleClose"
        >
          Cancel
        </ThemedButton>
        <ThemedButton
          :isLoading="addToUpcomingDelivery.isPending"
          theme="gray"
          @click.prevent="addToUpcomingDelivery.execute"
        >
          Submit
        </ThemedButton>
      </div>
    </template>
  </Modal>
</template>
